import React, { useState, useEffect, useRef } from 'react';
import Header from './Header';
import Footer from './Footer';
import ProductCard from './ProductCard';
import PageLayout from './PageLayout';
import ProductCardSkeleton from './ProductCardSkeleton';
import SEOHead from './SEOHead';
import FAQBox from './FAQBox';
import { apiGet } from '../utils/api';
import { MagnifyingGlassIcon, FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useDarkMode } from '../contexts/DarkModeContext';

// Minimal production-only client error reporter
const safeReportError = async (message, context = {}) => {
  try {
    if (process.env.NODE_ENV !== 'production') return;
    await fetch('/api/client-logs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message: String(message), context: { location: window?.location?.pathname || '/', ...context }, ts: Date.now() }),
      keepalive: true,
    });
  } catch (_) {}
};

const PRODUCTS_PER_PAGE = 20;


// Hero Banner Component
const HeroBanner = ({ isDarkMode, filterBarRef }) => (
  <div className={`py-2 sm:py-6 lg:py-8 transition-colors duration-300 ${
    isDarkMode 
      ? 'bg-[#0f0f0f]' 
      : 'bg-gradient-to-br from-blue-50 to-white'
  }`}>
    <div className="max-w-[1525px] mx-auto px-4 sm:px-5">
      <div className="w-full flex justify-center relative" style={{zIndex: 1}}>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{zIndex: 0}}>
          <img 
            src="/images/categories/tiktok.svg" 
            alt="TikTok background" 
            width="320" height="320"
            style={{
              width: '200px',
              height: '200px',
              filter: 'blur(40px)',
              opacity: isDarkMode ? 0.15 : 0.12,
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
            className="sm:w-[320px] sm:h-[320px] sm:blur-[50px]"
            loading="eager"
            decoding="async"
          />
        </div>
        <div className={`rounded-2xl sm:rounded-3xl p-4 sm:p-8 lg:p-10 flex flex-col items-center justify-center text-white w-full mx-4 max-w-[1495px] mx-auto relative transition-all duration-500 ${
          isDarkMode 
            ? 'bg-gradient-to-br from-[#0a0a0a] via-[#1a1a1a] to-[#2a2a2a] shadow-2xl shadow-black/50 border border-cyan-500/20' 
            : 'bg-blue-900 shadow-lg'
        }`} style={{zIndex: 1}}>
          {/* Animated background elements for dark mode */}
          {isDarkMode && (
            <div className="absolute inset-0 overflow-hidden rounded-2xl sm:rounded-3xl">
              <div className="absolute -top-6 -left-6 sm:-top-10 sm:-left-10 w-12 h-12 sm:w-20 sm:h-20 bg-cyan-400/10 rounded-full blur-xl animate-pulse"></div>
              <div className="absolute -bottom-6 -right-6 sm:-bottom-10 sm:-right-10 w-20 h-20 sm:w-32 sm:h-32 bg-pink-300/10 rounded-full blur-xl animate-pulse" style={{animationDelay: '1s'}}></div>
              <div className="absolute top-1/2 left-1/4 w-10 h-10 sm:w-16 sm:h-16 bg-blue-500/10 rounded-full blur-lg animate-pulse" style={{animationDelay: '2s'}}></div>
              <div className="absolute top-1/4 right-1/4 w-8 h-8 sm:w-12 sm:h-12 bg-yellow-400/10 rounded-full blur-lg animate-pulse" style={{animationDelay: '0.5s'}}></div>
              <div className="absolute bottom-1/4 left-1/3 w-10 h-10 sm:w-14 sm:h-14 bg-green-400/10 rounded-full blur-lg animate-pulse" style={{animationDelay: '1.5s'}}></div>
            </div>
          )}
          
          <div className="relative z-10">
            <h1 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold mb-2 sm:mb-4 text-center transition-colors duration-300 ${
              isDarkMode 
                ? 'text-white drop-shadow-lg' 
                : 'text-white'
            }`}>
              Buy & Sell TikTok Accounts
            </h1>
            <p className={`text-sm sm:text-base md:text-lg lg:text-xl mb-4 sm:mb-8 text-center max-w-2xl transition-colors duration-300 ${
              isDarkMode 
                ? 'text-cyan-100' 
                : 'text-blue-50'
            }`}>
              Buy monetized & affiliate shop enabled TikTok accounts with secure escrow and fast transfers, Grow your TikTok faster with <span className={`font-bold transition-colors duration-300 ${
                isDarkMode ? 'text-yellow-300' : 'text-yellow-400'
              }`}>YTProud</span>.
            </p>
            
            {/* Modern Feature Buttons */}
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mt-6 sm:mt-8">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-cyan-900/30 border-cyan-500/50 text-white' 
                  : 'bg-cyan-100 border-cyan-300 text-cyan-800'
              }`}>
                <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
                <span className="text-sm font-medium">Instant Transfer</span>
              </div>
              
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-blue-900/30 border-blue-500/50 text-white' 
                  : 'bg-blue-100 border-blue-300 text-blue-800'
              }`}>
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-sm font-medium">Escrow Protection</span>
              </div>
              
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-pink-900/30 border-pink-500/50 text-white' 
                  : 'bg-pink-100 border-pink-300 text-pink-800'
              }`}>
                <div className="w-2 h-2 bg-pink-500 rounded-full"></div>
                <span className="text-sm font-medium">Verified Accounts</span>
              </div>
              
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-yellow-900/30 border-yellow-500/50 text-white' 
                  : 'bg-yellow-100 border-yellow-300 text-yellow-800'
              }`}>
                <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                <span className="text-sm font-medium">24/7 Support</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const BuyTiktokAccount = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [seoSettings, setSeoSettings] = useState(null);
  const [seoLoaded, setSeoLoaded] = useState(false);
  const [filters, setFilters] = useState({
    category: 'all',
    minPrice: '',
    maxPrice: '',
    minFollowers: '',
    maxFollowers: '',
    minIncome: '',
    maxIncome: '',
    monetization: 'all',
    sortBy: 'latest',
    search: ''
  });
  const filterBarRef = useRef(null);
  const { isDarkMode } = useDarkMode();
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  // Fetch SEO settings for buy TikTok account page
  useEffect(() => {
    const fetchSeoSettings = async () => {
      try {
        const res = await apiGet('/api/page-seo/page/buy-tiktok-account');
        const data = await res.json();
        setSeoSettings(data);
        setSeoLoaded(true); // ✅ Set loaded on success
      } catch (error) {
        safeReportError('buy_tt_seo_fetch_failed');
        setSeoLoaded(true); // ✅ Set loaded on error too
      }
    };
    fetchSeoSettings();
  }, []);

  // Fetch TikTok products from backend
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        
        // Check cache first
        let cachedData = null;
        let cacheTime = null;
        try {
          cachedData = sessionStorage.getItem('tiktok_products');
          cacheTime = sessionStorage.getItem('tiktok_products_time');
        } catch (_) {
          safeReportError('buy_tt_cache_access_failed');
        }
        
        // Use cache if it's less than 5 minutes old
        if (cachedData && cacheTime && (Date.now() - parseInt(cacheTime)) < 300000) {
          try {
            const data = JSON.parse(cachedData);
            setProducts(Array.isArray(data) ? data : []);
          } catch (_) {
            safeReportError('buy_tt_cache_parse_failed');
            setProducts([]);
          }
          setLoading(false);
          return;
        }
        
        let productsData = [];
        try {
          const res = await apiGet('/api/products?status=published&limit=50');
          if (res.ok) {
            productsData = await res.json();
          } else {
            const fallback = await apiGet('/api/products/boosted-random');
            if (fallback.ok) {
              productsData = await fallback.json();
            } else {
              const errText = await res.text();
              throw new Error(errText || 'Failed to fetch products');
            }
          }
        } catch (innerErr) {
          try {
            const fallback = await apiGet('/api/products/boosted-random');
            if (fallback.ok) {
              productsData = await fallback.json();
            } else {
              const errText = await fallback.text();
              throw new Error(errText || innerErr.message);
            }
          } catch (fallbackErr) {
            safeReportError('buy_tt_products_fetch_failed');
            throw fallbackErr;
          }
        }
        
        const products = Array.isArray(productsData) ? productsData : [];
        setProducts(products);
        
        // Cache the data
        try {
          sessionStorage.setItem('tiktok_products', JSON.stringify(products));
          sessionStorage.setItem('tiktok_products_time', Date.now().toString());
        } catch (_) {
          safeReportError('buy_tt_cache_write_failed');
        }
        
        setError('');
      } catch (err) {
        setError('Failed to load products. Please try again later.');
        safeReportError('buy_tt_products_fetch_failed');
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Filter products based on selected filters
  const filteredProducts = products
    .filter(product => {
      const type = String(product.accountType || product.platform || '').toLowerCase();
      return type.includes('tiktok');
    })
    .filter(product => {
      if (filters.category !== 'all' && product.stats?.category !== filters.category) return false;
      if (filters.minPrice && product.price < Number(filters.minPrice)) return false;
      if (filters.maxPrice && product.price > Number(filters.maxPrice)) return false;
      if (filters.minFollowers && product.stats?.followers < Number(filters.minFollowers)) return false;
      if (filters.maxFollowers && product.stats?.followers > Number(filters.maxFollowers)) return false;
      if (filters.minIncome && product.stats?.monthlyIncome < Number(filters.minIncome)) return false;
      if (filters.maxIncome && product.stats?.monthlyIncome > Number(filters.maxIncome)) return false;
      if (filters.monetization !== 'all') {
        const monetizedStatus = product.stats?.monetized || product.stats?.monetization || product.monetized || product.monetization;
        const isMonetized = String(monetizedStatus).toLowerCase() === 'yes' || String(monetizedStatus).toLowerCase() === 'monetized' || monetizedStatus === true;
        if (filters.monetization === 'monetized' && !isMonetized) return false;
        if (filters.monetization === 'non-monetized' && isMonetized) return false;
      }
      if (filters.search && !product.title.toLowerCase().includes(filters.search.toLowerCase())) return false;
      return true;
    });

  // Sort products
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (filters.sortBy === 'price-low-high') return a.price - b.price;
    if (filters.sortBy === 'price-high-low') return b.price - a.price;
    return (b.createdAt || b._id) > (a.createdAt || a._id) ? 1 : -1;
  });

  const paginatedProducts = sortedProducts.slice(
    (currentPage - 1) * PRODUCTS_PER_PAGE,
    currentPage * PRODUCTS_PER_PAGE
  );

  const totalPages = Math.ceil(sortedProducts.length / PRODUCTS_PER_PAGE);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const renderPagination = () => (
    <div className="flex items-center gap-4 mt-6 sm:mt-10">
      {[...Array(totalPages)].map((_, idx) => (
        <button
          key={idx}
          onClick={() => setCurrentPage(idx + 1)}
          className={`w-14 h-12 rounded-full border text-lg font-medium transition-colors duration-150 focus:outline-none ${
            currentPage === idx + 1
              ? 'bg-red-600 text-white border-red-600'
              : isDarkMode
                ? 'bg-[#2a2a2a] text-gray-200 border-gray-600 hover:bg-[#3a3a3a]'
                : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-100'
          }`}
        >
          {idx + 1}
        </button>
      ))}
      <button
        onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
        disabled={currentPage === totalPages}
        className={`w-20 h-12 rounded-full border text-lg font-medium transition-colors duration-150 focus:outline-none ${
          currentPage === totalPages
            ? isDarkMode
              ? 'bg-[#1a1a1a] text-gray-500 border-gray-700 cursor-not-allowed'
              : 'bg-gray-200 text-gray-400 border-gray-200 cursor-not-allowed'
            : 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
        }`}
      >
        Next
      </button>
    </div>
  );

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  // Open mobile filters from footer button
  useEffect(() => {
    const openHandler = () => setMobileFilterOpen(true);
    window.addEventListener('openMobileFilters', openHandler);
    return () => window.removeEventListener('openMobileFilters', openHandler);
  }, []);

  const handleResetFilters = () => {
    setFilters({
      category: 'all',
      minPrice: '',
      maxPrice: '',
      minFollowers: '',
      maxFollowers: '',
      minIncome: '',
      maxIncome: '',
      monetization: 'all',
      sortBy: 'latest',
      search: ''
    });
    setCurrentPage(1);
  };

  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-300 ${
      isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-50'
    }`}>
      <SEOHead
        title={seoSettings?.title || 'Buy TikTok Account - YTProud'}
        description={seoSettings?.description || 'Buy verified TikTok accounts with followers and views. Secure marketplace for TikTok account purchases.'}
        keywords={seoSettings?.keywords || 'buy tiktok account, tiktok account for sale, verified tiktok accounts'}
        ogTitle={seoSettings?.ogTitle || 'Buy TikTok Account - YTProud'}
        ogDescription={seoSettings?.ogDescription || 'Buy verified TikTok accounts with followers and views'}
        ogImage={seoSettings?.ogImage || '/images/tiktok-og.jpg'}
        schemaEnabled={seoSettings?.schemaEnabled || false}
        schemaJsonLd={seoSettings?.customSchemaJsonLd}
        twitterTitle={seoSettings?.twitterTitle || 'Buy TikTok Account - YTProud'}
        twitterDescription={seoSettings?.twitterDescription || 'Buy verified TikTok accounts with followers and views'}
        twitterImage={seoSettings?.twitterImage || '/images/tiktok-og.jpg'}
        canonical={seoSettings?.canonical || 'https://ytproud.com/buy-tiktok-account'}
        noindex={seoSettings?.noindex || false}
        nofollow={seoSettings?.nofollow || false}
      />
      <Header />
      {/* Modern Hero Section - Banner */}
      <HeroBanner isDarkMode={isDarkMode} filterBarRef={filterBarRef} />
      <PageLayout>
        {/* Desktop Filter Bar (visible on desktop only) */}
        <div className="hidden lg:block">
          <div ref={filterBarRef} className={`rounded-2xl shadow-lg p-6 lg:p-8 w-full mx-4 max-w-[1495px] mx-auto mb-4 sm:mb-8 transition-colors duration-300 ${
            isDarkMode ? 'bg-[#282828] shadow-2xl shadow-black/30' : 'bg-white'
          }`}>
            <div className="flex flex-col gap-y-4 lg:gap-y-6 w-full">
              {/* Row 1 - Search, Followers, Income, Price */}
              <div className="grid grid-cols-1 lg:grid-cols-4 items-center gap-3 lg:gap-4 xl:gap-5 w-full">
                {/* Search */}
                <div className="col-span-1 flex flex-col items-start">
                  <span className={`text-sm lg:text-base font-semibold mb-1 ml-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>Name/Type</span>
                  <div className={`flex items-center gap-2 lg:gap-3 px-3 lg:px-4 xl:px-5 py-3 lg:py-4 rounded-full font-semibold shadow-sm border-none focus-within:ring-2 transition outline-none w-full text-sm lg:text-base ${
                    isDarkMode ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus-within:bg-[#2a2a2a] focus-within:ring-white/20' : 'bg-gray-50 text-gray-700 hover:bg-gray-100 focus-within:bg-gray-100 focus-within:ring-black/10'
                  }`}>
                    <MagnifyingGlassIcon className="w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      name="search"
                      value={filters.search}
                      onChange={handleFilterChange}
                      placeholder="Search TikTok accounts..."
                      className={`bg-transparent w-full px-1 py-1 outline-none border-none text-sm lg:text-base ${
                        isDarkMode ? 'text-gray-200 placeholder-gray-400' : 'text-gray-700 placeholder-gray-500'
                      }`}
                    />
                  </div>
                </div>
                {/* Followers */}
                <div className="col-span-1 flex flex-col items-start">
                  <span className={`text-sm lg:text-base font-semibold mb-1 ml-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>Followers</span>
                  <div className={`flex items-center gap-2 lg:gap-3 px-3 lg:px-4 xl:px-5 py-3 lg:py-4 rounded-full font-semibold shadow-sm border-none transition outline-none text-sm lg:text-base w-full ${
                    isDarkMode ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus-within:bg-[#2a2a2a] focus-within:ring-2 focus-within:ring-white/20' : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus-within:bg-gray-100 focus-within:ring-2 focus-within:ring-black/10'
                  }`}>
                    <span>👥</span>
                    <input
                      type="number"
                      name="minFollowers"
                      value={filters.minFollowers}
                      onChange={handleFilterChange}
                      min="0"
                      placeholder="Min"
                      className={`flex-1 min-w-0 px-2 lg:px-3 py-2 rounded-lg outline-none text-sm lg:text-base text-center shadow-sm ${
                        isDarkMode ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                      }`}
                    />
                    <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>-</span>
                    <input
                      type="number"
                      name="maxFollowers"
                      value={filters.maxFollowers}
                      onChange={handleFilterChange}
                      min="0"
                      placeholder="Max"
                      className={`flex-1 min-w-0 px-2 lg:px-3 py-2 rounded-lg outline-none text-sm lg:text-base text-center shadow-sm ${
                        isDarkMode ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                      }`}
                    />
                  </div>
                </div>
                {/* Income */}
                <div className="col-span-1 flex flex-col items-start">
                  <span className={`text-sm lg:text-base font-semibold mb-1 ml-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>Income</span>
                  <div className={`flex items-center gap-2 lg:gap-3 px-3 lg:px-4 xl:px-5 py-3 lg:py-4 rounded-full font-semibold shadow-sm border-none transition outline-none text-sm lg:text-base w-full ${
                    isDarkMode ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus-within:bg-[#2a2a2a] focus-within:ring-2 focus-within:ring-white/20' : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus-within:bg-gray-100 focus-within:ring-2 focus-within:ring-black/10'
                  }`}>
                    <span>💰</span>
                    <input
                      type="number"
                      name="minIncome"
                      value={filters.minIncome}
                      onChange={handleFilterChange}
                      min="0"
                      placeholder="Min"
                      className={`flex-1 min-w-0 px-2 lg:px-3 py-2 rounded-lg outline-none text-sm lg:text-base text-center shadow-sm ${
                        isDarkMode ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                      }`}
                    />
                    <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>-</span>
                    <input
                      type="number"
                      name="maxIncome"
                      value={filters.maxIncome}
                      onChange={handleFilterChange}
                      min="0"
                      placeholder="Max"
                      className={`flex-1 min-w-0 px-2 lg:px-3 py-2 rounded-lg outline-none text-sm lg:text-base text-center shadow-sm ${
                        isDarkMode ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                      }`}
                    />
                  </div>
                </div>
                {/* Price */}
                <div className="col-span-1 flex flex-col items-start">
                  <span className={`text-sm lg:text-base font-semibold mb-1 ml-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>Price</span>
                  <div className={`flex items-center gap-2 lg:gap-3 px-3 lg:px-4 xl:px-5 py-3 lg:py-4 rounded-full font-semibold shadow-sm border-none transition outline-none text-sm lg:text-base w-full ${
                    isDarkMode ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus-within:bg-[#2a2a2a] focus-within:ring-2 focus-within:ring-white/20' : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus-within:bg-gray-100 focus-within:ring-2 focus-within:ring-black/10'
                  }`}>
                    <span>$</span>
                    <input
                      type="number"
                      name="minPrice"
                      value={filters.minPrice}
                      onChange={handleFilterChange}
                      min="0"
                      placeholder="Min"
                      className={`flex-1 min-w-0 px-2 lg:px-3 py-2 rounded-lg outline-none text-sm lg:text-base text-center shadow-sm ${
                        isDarkMode ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                      }`}
                    />
                    <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>-</span>
                    <input
                      type="number"
                      name="maxPrice"
                      value={filters.maxPrice}
                      onChange={handleFilterChange}
                      min="0"
                      placeholder="Max"
                      className={`flex-1 min-w-0 px-2 lg:px-3 py-2 rounded-lg outline-none text-sm lg:text-base text-center shadow-sm ${
                        isDarkMode ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                      }`}
                    />
                  </div>
                </div>
              </div>
              {/* Row 2 - Category, Monetization, Sort, Reset */}
              <div className="grid grid-cols-1 lg:grid-cols-4 items-center gap-3 lg:gap-4 xl:gap-6 w-full">
                {/* Category */}
                <div className="col-span-1">
            <select 
              name="category"
              value={filters.category}
              onChange={handleFilterChange}
                    className={`px-4 lg:px-6 xl:px-7 py-3 lg:py-4 rounded-full font-semibold shadow-sm border-none transition outline-none text-sm lg:text-base w-full ${
                      isDarkMode ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus:bg-[#2a2a2a] focus:ring-2 focus:ring-white/20' : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus:bg-gray-100 focus:ring-2 focus:ring-black/10'
                    }`}
            >
              <option value="all">All Categories</option>
              <option value="Comedy">Comedy</option>
              <option value="Music">Music</option>
              <option value="Dance">Dance</option>
              <option value="Education">Education</option>
              <option value="Fashion & Style">Fashion & Style</option>
              <option value="Fitness & Sports">Fitness & Sports</option>
              <option value="Gaming">Gaming</option>
              <option value="Pets & Animals">Pets & Animals</option>
              <option value="Travel">Travel</option>
              <option value="Vlogs">Vlogs</option>
              <option value="Models & Celebrities">Models & Celebrities</option>
            </select>
                </div>
                {/* Monetization */}
                <div className="col-span-1">
            <select 
              name="monetization"
              value={filters.monetization}
              onChange={handleFilterChange}
                    className={`px-4 lg:px-6 xl:px-7 py-3 lg:py-4 rounded-full font-semibold shadow-sm border-none transition outline-none text-sm lg:text-base w-full ${
                      isDarkMode ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus:bg-[#2a2a2a] focus:ring-2 focus:ring-white/20' : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus:bg-gray-100 focus:ring-2 focus:ring-black/10'
                    }`}
            >
              <option value="all">All Monetization</option>
              <option value="monetized">Monetized</option>
              <option value="non-monetized">Non-Monetized</option>
            </select>
                </div>
                {/* Sort */}
                <div className="col-span-1">
                  <select
                    name="sortBy"
                    value={filters.sortBy}
              onChange={handleFilterChange}
                    className={`px-4 lg:px-6 xl:px-7 py-3 lg:py-4 rounded-full font-semibold shadow-sm border transition outline-none text-sm lg:text-base w-full ${
                      isDarkMode ? 'bg-[#1a1a1a] text-gray-200 border-white/20 focus:ring-2 focus:ring-white/20 focus:border-white/40' : 'bg-white text-gray-900 border-black/10 focus:ring-2 focus:ring-black/10 focus:border-black/20'
                    }`}
                  >
                    <option value="latest">Latest</option>
                    <option value="price-low-high">Price: Low to High</option>
                    <option value="price-high-low">Price: High to Low</option>
                  </select>
                </div>
                {/* Reset */}
                <div className="col-span-1">
                  <button
                    onClick={handleResetFilters}
                    className={`px-4 lg:px-6 xl:px-7 py-3 lg:py-4 rounded-full font-bold shadow-sm transition border-none text-sm lg:text-base w-full max-w-[200px] mx-auto lg:mx-0 xl:mx-0 ${
                      isDarkMode ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-900 text-white hover:bg-blue-800'
                    }`}
                  >
                    Reset Filters
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>


        {/* Product Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3 lg:gap-6 max-w-[1495px] mx-auto mt-6 sm:mt-12">
            {Array.from({ length: 20 }).map((_, i) => <ProductCardSkeleton key={i} />)}
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-red-600 text-lg">{error}</div>
          </div>
        ) : paginatedProducts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 sm:py-24 text-center text-gray-500">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z" />
            </svg>
            <div className="text-xl font-semibold mb-2">No accounts match your filters</div>
            <div className="text-base text-gray-400">Try adjusting your search or filter options.</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3 lg:gap-6 max-w-[1495px] mx-auto">
            {paginatedProducts.map((product, i) => (
              <ProductCard
                key={product._id || i + (currentPage - 1) * PRODUCTS_PER_PAGE}
                product={product}
                width={360}
              />
            ))}
          </div>
        )}
        {/* Pagination Controls */}
        {!loading && !error && paginatedProducts.length > 0 && (
          <div className="max-w-[1495px] mx-auto">{renderPagination()}</div>
        )}
      </PageLayout>
      {/* FAQ Section - TikTok specific */}
      <FAQBox
        title="TikTok Buying FAQs"
        faqs={[
          { 
            q: 'Why choose YTProud to buy a TikTok account?', 
            a: 'YTProud is a trusted marketplace for buying verified TikTok accounts, including monetized, Shop-enabled, and aged profiles. Thousands of creators and businesses choose us for fast delivery, buyer protection, and a wide range of account options.\n\n👉 <a href="/buy-tiktok-account" class="underline text-blue-600 hover:text-blue-800">Start browsing TikTok accounts today.</a>' 
          },
          { 
            q: 'Is it legal to buy TikTok accounts?', 
            a: 'Yes. Buying a TikTok account is legal as long as it follows TikTok\'s terms of service. At YTProud, transfers are done securely with proper ownership changes. We provide verified accounts for content creation, marketing, or business growth, ensuring safe and ethical use.' 
          },
          { 
            q: 'How do I buy a TikTok account from YTProud?', 
            a: 'Buying from YTProud is quick and secure:\n\n• Browse verified TikTok listings.\n• Pick an account that matches your goals.\n• Chat with the seller inside YTProud.\n• Pay securely through escrow.\n• Receive account details in 24–72 hours.\n\n👉 <a href="/buy-tiktok-account" class="underline text-blue-600 hover:text-blue-800">Explore TikTok accounts for sale.</a>' 
          },
          { 
            q: 'What types of TikTok accounts can I buy on YTProud?', 
            a: 'On YTProud, you can buy:\n\n• Monetized TikTok accounts\n• TikTok Shop affiliate accounts\n• Niche-specific accounts with high engagement\n• Aged TikTok accounts with real followers\n• TikTok Creativity Program accounts for revenue growth\n\nEach listing includes details like country, followers, and monetization eligibility.' 
          },
          { 
            q: 'Which TikTok account is best for me?', 
            a: 'It depends on your goals:\n\n• For earning money: Choose a monetized TikTok account.\n• For dropshipping/affiliate marketing: Pick a Shop-enabled account.\n• For viral reach: Get a niche account with high engagement.\n• For fast growth: Buy an aged account with active followers.\n\nYTProud helps match you with the best account for your strategy.' 
          },
        ]}
      />
      {/* Mobile Filter Drawer */}
      {mobileFilterOpen && (
        <div className="fixed inset-0 z-50 flex lg:hidden">
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black bg-opacity-40 transition-opacity"
            onClick={() => setMobileFilterOpen(false)}
          />
          {/* Drawer */}
          <div className={`ml-auto w-full max-w-sm h-full shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out ${
            isDarkMode ? 'bg-[#282828] shadow-black/50' : 'bg-white'
          }`}>
            <div className={`flex items-center justify-between p-4 border-b transition-colors duration-300 ${
              isDarkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <span className={`text-lg font-bold transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>Filters</span>
              <button onClick={() => setMobileFilterOpen(false)} aria-label="Close filters">
                <XMarkIcon className={`w-7 h-7 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`} />
              </button>
            </div>
            <div className="overflow-y-auto p-4 flex-1">
              {/* Search */}
              <div className="flex flex-col items-start w-full">
                <span className={`text-base font-semibold mb-1 ml-2 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>Name/Type</span>
                <div className={`flex items-center gap-3 px-4 py-3 rounded-full font-semibold shadow-sm border-none transition outline-none w-full text-base ${
                  isDarkMode ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus-within:bg-[#2a2a2a] focus-within:ring-2 focus-within:ring-white/20' : 'bg-gray-50 text-gray-700 hover:bg-gray-100 focus-within:bg-gray-100 focus-within:ring-2 focus-within:ring-black/10'
                }`}>
                  <MagnifyingGlassIcon className={`w-5 h-5 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-400'
                  }`} />
                  <input
                    type="text"
                    name="search"
                    value={filters.search}
                    onChange={handleFilterChange}
                    placeholder="Search TikTok accounts..."
                    className={`bg-transparent w-full px-1 py-1 outline-none border-none text-base transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-200 placeholder-gray-400' : 'text-gray-700 placeholder-gray-500'
                    }`}
                  />
                </div>
              </div>
              {/* Category */}
              <select
                name="category"
                value={filters.category}
                onChange={handleFilterChange}
                className={`mt-4 px-4 py-3 rounded-full font-semibold shadow-sm border-none transition outline-none text-base w-full ${
                  isDarkMode ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus:bg-[#2a2a2a] focus:ring-2 focus:ring-white/20' : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus:bg-gray-100 focus:ring-2 focus:ring-black/10'
                }`}
              >
                <option value="all">All Categories</option>
                <option value="Comedy">Comedy</option>
                <option value="Music">Music</option>
                <option value="Dance">Dance</option>
                <option value="Education">Education</option>
                <option value="Fashion & Style">Fashion & Style</option>
                <option value="Fitness & Sports">Fitness & Sports</option>
                <option value="Gaming">Gaming</option>
                <option value="Pets & Animals">Pets & Animals</option>
                <option value="Travel">Travel</option>
                <option value="Vlogs">Vlogs</option>
                <option value="Models & Celebrities">Models & Celebrities</option>
              </select>
              {/* Monetization */}
              <select
                name="monetization"
                value={filters.monetization}
                onChange={handleFilterChange}
                className={`mt-4 px-4 py-3 rounded-full font-semibold shadow-sm border-none transition outline-none text-base w-full ${
                  isDarkMode ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus:bg-[#2a2a2a] focus:ring-2 focus:ring-white/20' : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus:bg-gray-100 focus:ring-2 focus:ring-black/10'
                }`}
              >
                <option value="all">All Monetization</option>
                <option value="monetized">Monetized</option>
                <option value="non-monetized">Non-Monetized</option>
              </select>
              {/* Sort */}
              <select
                name="sortBy"
                value={filters.sortBy}
                onChange={handleFilterChange}
                className={`mt-4 px-4 py-3 rounded-full font-semibold shadow-sm border transition outline-none text-base w-full ${
                  isDarkMode ? 'bg-[#1a1a1a] text-gray-200 border-white/20 focus:ring-2 focus:ring-white/20 focus:border-white/40' : 'bg-white text-gray-900 border-black/10 focus:ring-2 focus:ring-black/10 focus:border-black/20'
                }`}
              >
                <option value="latest">Latest</option>
                <option value="price-low-high">Price: Low to High</option>
                <option value="price-high-low">Price: High to Low</option>
              </select>
              {/* Followers */}
              <div className="flex flex-col items-start w-full mt-4">
                <span className={`text-base font-semibold mb-1 ml-2 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>Followers</span>
                <div className={`flex items-center gap-6 px-4 py-3 rounded-full font-semibold shadow-sm border-none transition outline-none text-base w-full ${
                  isDarkMode ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus-within:bg-[#2a2a2a] focus-within:ring-2 focus-within:ring-white/20' : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus-within:bg-gray-100 focus-within:ring-2 focus-within:ring-black/10'
                }`}>
                  <span>👥</span>
                  <input
                    type="number"
                    name="minFollowers"
                    value={filters.minFollowers}
                    onChange={handleFilterChange}
                    min="0"
                    placeholder="Min"
                    className={`w-20 px-3 py-2 rounded-lg outline-none text-base text-center shadow-sm ${
                      isDarkMode ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                    }`}
                  />
                  <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>-</span>
                  <input
                    type="number"
                    name="maxFollowers"
                    value={filters.maxFollowers}
                    onChange={handleFilterChange}
                    min="0"
                    placeholder="Max"
                    className={`w-20 px-3 py-2 rounded-lg outline-none text-base text-center shadow-sm ${
                      isDarkMode ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                    }`}
                  />
                </div>
              </div>
              {/* Income */}
              <div className="flex flex-col items-start w-full mt-4">
                <span className={`text-base font-semibold mb-1 ml-2 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>Income</span>
                <div className={`flex items-center gap-6 px-4 py-3 rounded-full font-semibold shadow-sm border-none transition outline-none text-base w-full ${
                  isDarkMode ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus-within:bg-[#2a2a2a] focus-within:ring-2 focus-within:ring-white/20' : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus-within:bg-gray-100 focus-within:ring-2 focus-within:ring-black/10'
                }`}>
                  <span>💰</span>
                  <input
                    type="number"
                    name="minIncome"
                    value={filters.minIncome}
                    onChange={handleFilterChange}
                    min="0"
                    placeholder="Min"
                    className={`w-20 px-3 py-2 rounded-lg outline-none text-base text-center shadow-sm ${
                      isDarkMode ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                    }`}
                  />
                  <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>-</span>
                  <input
                    type="number"
                    name="maxIncome"
                    value={filters.maxIncome}
                    onChange={handleFilterChange}
                    min="0"
                    placeholder="Max"
                    className={`w-20 px-3 py-2 rounded-lg outline-none text-base text-center shadow-sm ${
                      isDarkMode ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                    }`}
                  />
                </div>
              </div>
              {/* Price */}
              <div className="flex flex-col items-start w-full mt-4">
                <span className={`text-base font-semibold mb-1 ml-2 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>Price</span>
                <div className={`flex items-center gap-6 px-4 py-3 rounded-full font-semibold shadow-sm border-none transition outline-none text-base w-full ${
                  isDarkMode ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus-within:bg-[#2a2a2a] focus-within:ring-2 focus-within:ring-white/20' : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus-within:bg-gray-100 focus-within:ring-2 focus-within:ring-black/10'
                }`}>
                  <span>$</span>
                  <input
                    type="number"
                    name="minPrice"
                    value={filters.minPrice}
                    onChange={handleFilterChange}
                    min="0"
                    placeholder="Min"
                    className={`w-20 px-3 py-2 rounded-lg outline-none text-base text-center shadow-sm ${
                      isDarkMode ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                    }`}
                  />
                  <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>-</span>
                  <input
                    type="number"
                    name="maxPrice"
                    value={filters.maxPrice}
                    onChange={handleFilterChange}
                    min="0"
                    placeholder="Max"
                    className={`w-20 px-3 py-2 rounded-lg outline-none text-base text-center shadow-sm ${
                      isDarkMode ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                    }`}
                  />
                </div>
              </div>
              {/* Actions */}
              <div className="flex gap-4 mt-6 md:hidden">
                <button
                  onClick={() => { handleResetFilters(); setMobileFilterOpen(false); }}
                  className={`flex-1 px-4 py-4 rounded-full font-bold shadow-sm transition border-none text-base ${
                    isDarkMode ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-900 text-white hover:bg-blue-800'
                  }`}
                >
                  Reset Filters
                </button>
                <button
                  onClick={() => setMobileFilterOpen(false)}
                  className={`flex-1 px-4 py-4 rounded-full font-bold shadow-sm transition border-none text-base ${
                    isDarkMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  Search
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
};

export default BuyTiktokAccount;